import { Injectable } from '@angular/core';
import {
  BelpostContact,
  BelpostPerson,
  BelpostIndividualPerson,
  BelpostLegalPerson,
} from '$shared/types/belpost-contact';
import { BelpostRepublicAddress } from '$shared/types/belpost-republic-address';
import { BelpostInternationalAddress } from '$shared/types/belpost-international-address';
import {
  WidgetContact,
  WidgetPerson,
  WidgetIndividualInfo,
  WidgetLegalInfo,
  WidgetRepublicAddress,
  WidgetInternationalAddress,
  WidgetCountry,
  WidgetRepublicAddressInfo,
  WidgetRepublicSubscriberBoxAddressInfo,
  WidgetRepublicOnDemandAddressInfo,
} from '../model/address-widget-picker-contact';
import { ContactAddressType } from '../model/address-map-type';

/**
 * Сервис-адаптер для преобразования WidgetContact в BelpostContact.
 * Обеспечивает трансформацию данных из виджета в формат API Belpost.
 */
@Injectable({
  providedIn: 'root',
})
export class WidgetToBelpostAdapterService {
  constructor() {}

  /**
   * Преобразует контакт виджета в формат Belpost.
   *
   * @param widgetContact - Контакт из виджета
   * @returns Контакт в формате Belpost
   */
  public transform(widgetContact: WidgetContact): BelpostContact {
    return {
      id: widgetContact.id ? widgetContact.id : null,
      person: widgetContact.person
        ? this.transformPerson(widgetContact.person)
        : null,
      address: widgetContact.address
        ? this.transformAddress(widgetContact.address)
        : null,
      isValid: widgetContact.isValid,
    };
  }

  /**
   * Преобразует персону виджета в формат Belpost.
   *
   * @param widgetPerson - Персона из виджета
   * @returns Персона в формате Belpost
   */
  private transformPerson(widgetPerson: WidgetPerson): BelpostPerson {
    if (widgetPerson.type === 'individual') {
      return this.transformIndividualPerson(widgetPerson);
    } else {
      return this.transformLegalPerson(widgetPerson);
    }
  }

  /**
   * Преобразует физическое лицо виджета в формат Belpost.
   *
   * @param individualInfo - Физическое лицо из виджета
   * @returns Физическое лицо в формате Belpost
   */
  private transformIndividualPerson(
    individualInfo: WidgetIndividualInfo
  ): BelpostIndividualPerson {
    return {
      type: 'individual',
      first_name: individualInfo.firstName,
      last_name: individualInfo.lastName,
      second_name: individualInfo.secondName,
      email: individualInfo.email,
      phone: individualInfo.phone,
    };
  }

  /**
   * Преобразует юридическое лицо виджета в формат Belpost.
   *
   * @param legalInfo - Юридическое лицо из виджета
   * @returns Юридическое лицо в формате Belpost
   */
  private transformLegalPerson(legalInfo: WidgetLegalInfo): BelpostLegalPerson {
    return {
      type: legalInfo.type === 'legal' ? 'legal' : 'individual_entrepreneur',
      organization_name: legalInfo.organizationName,
      email: legalInfo.email,
      phone: legalInfo.phone,
    };
  }

  /**
   * Преобразует адрес виджета в формат Belpost.
   *
   * @param widgetAddress - Адрес из виджета
   * @returns Адрес в формате Belpost
   */
  private transformAddress(
    widgetAddress: WidgetRepublicAddress | WidgetInternationalAddress
  ): BelpostRepublicAddress | BelpostInternationalAddress {
    if (this.isRepublicAddress(widgetAddress)) {
      return this.transformRepublicAddress(widgetAddress);
    } else {
      return this.transformInternationalAddress(widgetAddress);
    }
  }

  /**
   * Проверяет, является ли адрес международным.
   *
   * @param address - Адрес для проверки
   * @returns true, если адрес международный
   */
  private isRepublicAddress(
    address: WidgetRepublicAddress | WidgetInternationalAddress
  ): address is WidgetRepublicAddress {
    return 'externalId' in address;
  }

  /**
   * Преобразует международный адрес виджета в формат Belpost.
   *
   * @param internationalAddress - Международный адрес из виджета
   * @returns Международный адрес в формате Belpost
   */
  private transformInternationalAddress(
    internationalAddress: WidgetInternationalAddress
  ): BelpostInternationalAddress {
    return {
      postcode: internationalAddress.postcode,
      latitude: internationalAddress.latitude
        ? Number(internationalAddress.latitude)
        : null,
      longitude: internationalAddress.longitude
        ? Number(internationalAddress.longitude)
        : null,
      city: internationalAddress.city,
      flat: internationalAddress.flat,
      address_line: internationalAddress.addressLine,
      full_address: internationalAddress.fullAddress,
      country: this.transformCountry(internationalAddress.country),
      type: 'address' as Extract<ContactAddressType, 'address'>,
    };
  }

  /**
   * Преобразует республиканский адрес виджета в формат Belpost.
   *
   * @param republicAddress - Республиканский адрес из виджета
   * @returns Республиканский адрес в формате Belpost
   */
  private transformRepublicAddress(
    republicAddress: WidgetRepublicAddress
  ): BelpostRepublicAddress {
    if (this.isRepublicAddressInfo(republicAddress)) {
      return this.transformRepublicAddressInfo(republicAddress);
    }

    if (this.isRepublicOnDemandAddress(republicAddress)) {
      return this.transformRepublicOnDemandAddress(republicAddress);
    }

    if (this.isRepublicSubscriberBoxAddress(republicAddress)) {
      return this.transformRepublicSubscriberBoxAddress(republicAddress);
    }
  }

  /**
   * Проверяет, является ли адрес обычным республиканским адресом.
   */
  private isRepublicAddressInfo(
    address: WidgetRepublicAddress
  ): address is WidgetRepublicAddressInfo {
    return address.type === 'address';
  }

  /**
   * Проверяет, является ли адрес адресом до востребования.
   */
  private isRepublicOnDemandAddress(
    address: WidgetRepublicAddress
  ): address is WidgetRepublicOnDemandAddressInfo {
    return address.type === 'on-demand';
  }

  /**
   * Проверяет, является ли адрес адресом абонентского ящика.
   */
  private isRepublicSubscriberBoxAddress(
    address: WidgetRepublicAddress
  ): address is WidgetRepublicSubscriberBoxAddressInfo {
    return address.type === 'subscriber-box';
  }

  /**
   * Преобразует обычный республиканский адрес.
   */
  private transformRepublicAddressInfo(
    addressInfo: WidgetRepublicAddressInfo
  ): BelpostRepublicAddress {
    return {
      postcode: '', // Заглушка - не используется в виджете
      region: '', // Заглушка
      district: '', // Заглушка
      city: '', // Заглушка
      city_type: '', // Заглушка
      street_type: '', // Заглушка
      street: '', // Заглушка
      house: addressInfo.house,
      block: addressInfo.block,
      flat: addressInfo.flat,
      type: 'address',
      country_code: 'BY',
      full_address: addressInfo.fullAddress,
      floor: null,
      entrance: null,
      door_code: null,
      latitude: null,
      longitude: null,
      country: this.transformCountry(addressInfo.country),
      external_id: addressInfo.externalId,
      cell_number: null,
    };
  }

  /**
   * Преобразует адрес до востребования.
   */
  private transformRepublicOnDemandAddress(
    onDemandAddress: WidgetRepublicOnDemandAddressInfo
  ): BelpostRepublicAddress {
    return {
      postcode: '', // Заглушка
      region: '', // Заглушка
      district: '', // Заглушка
      city: '', // Заглушка
      city_type: '', // Заглушка
      street_type: '', // Заглушка
      street: '', // Заглушка
      house: onDemandAddress.house,
      block: null,
      flat: null,
      type: 'on_demand',
      country_code: 'BY',
      full_address: onDemandAddress.fullAddress,
      floor: null,
      entrance: null,
      door_code: null,
      latitude: null,
      longitude: null,
      country: this.transformCountry(onDemandAddress.country),
      external_id: onDemandAddress.externalId,
      cell_number: null,
    };
  }

  /**
   * Преобразует адрес абонентского ящика.
   */
  private transformRepublicSubscriberBoxAddress(
    subscriberBoxAddress: WidgetRepublicSubscriberBoxAddressInfo
  ): BelpostRepublicAddress {
    return {
      postcode: '', // Заглушка
      region: '', // Заглушка
      district: '', // Заглушка
      city: '', // Заглушка
      city_type: '', // Заглушка
      street_type: '', // Заглушка
      street: '', // Заглушка
      house: subscriberBoxAddress.house,
      block: null,
      flat: null,
      type: 'subscriber_box',
      country_code: 'BY',
      full_address: subscriberBoxAddress.fullAddress,
      floor: null,
      entrance: null,
      door_code: null,
      latitude: null,
      longitude: null,
      country: this.transformCountry(subscriberBoxAddress.country),
      external_id: subscriberBoxAddress.externalId,
      cell_number: subscriberBoxAddress.cellNumber
        ? parseInt(subscriberBoxAddress.cellNumber, 10)
        : null,
    };
  }

  /**
   * Преобразует страну виджета в формат Belpost.
   *
   * @param widgetCountry - Страна из виджета
   * @returns Страна в формате Belpost или null
   */
  private transformCountry(widgetCountry: WidgetCountry | null): any {
    if (!widgetCountry) {
      return null;
    }

    return {
      code: widgetCountry.code,
      name: widgetCountry.name,
      names: {
        ru: widgetCountry.names.ru,
        en: widgetCountry.names.en,
        by: widgetCountry.names.be,
      },
      is_popular: widgetCountry.isPopular,
      input_ru_lang: widgetCountry.availableLocales.ru,
      input_en_lang: widgetCountry.availableLocales.en,
    };
  }
}
